/**
 * Runtime configuration.
 *
 * Service base URLs are NOT baked in at build time. CI writes a runtime-config.json
 * into dist/ during deploy (see .github/workflows), so one build artifact can be
 * promoted dev → qa → prod unchanged.
 *
 * This module must be resolved BEFORE the app renders. The legacy client computed its
 * base URLs synchronously while the config fetch was still in flight, so the first
 * requests of a session could go to the wrong origin. `loadRuntimeConfig()` is awaited
 * in main.tsx and `getRuntimeConfig()` throws if anything reads it too early.
 */

export interface RuntimeConfig {
  /** Base URL for ingestion_service (:8001 locally). */
  readonly ingestionServiceUrl: string;
  /** Base URL for enrichment_service (:8002 locally). */
  readonly enrichmentServiceUrl: string;
}

const RUNTIME_CONFIG_PATH = '/runtime-config.json';

/**
 * In dev everything goes through the Vite proxy, which sidesteps CORS entirely.
 * In production the app calls the service origins directly, so those origins must be
 * present in each service's CORS_ORIGINS (allow_credentials=True means no wildcard).
 */
const DEV_CONFIG: RuntimeConfig = {
  ingestionServiceUrl: '/__proxy/ingestion',
  enrichmentServiceUrl: '/__proxy/enrichment',
};

let resolved: RuntimeConfig | null = null;

const trimTrailingSlash = (value: string): string => value.trim().replace(/\/+$/, '');

/** Reads the first non-empty string among `keys`. Tolerates legacy key spellings. */
function pick(source: Record<string, unknown>, keys: readonly string[]): string {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim() !== '') {
      return trimTrailingSlash(value);
    }
  }
  return '';
}

export class RuntimeConfigError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RuntimeConfigError';
  }
}

/**
 * Fetches and validates the runtime config. Resolves once; later calls return the
 * same value. Throws rather than falling back to a wrong origin — a misconfigured
 * deploy should fail visibly at boot, not produce an app that silently talks to
 * the wrong backend.
 */
export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  if (resolved) return resolved;

  if (import.meta.env.DEV) {
    resolved = DEV_CONFIG;
    return resolved;
  }

  let payload: Record<string, unknown>;
  try {
    const response = await fetch(`${RUNTIME_CONFIG_PATH}?v=${Date.now()}`, {
      cache: 'no-store',
      headers: { accept: 'application/json' },
    });
    if (!response.ok) {
      throw new RuntimeConfigError(
        `${RUNTIME_CONFIG_PATH} returned HTTP ${response.status}. The deploy did not write it.`,
      );
    }
    const parsed: unknown = await response.json();
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      throw new RuntimeConfigError(`${RUNTIME_CONFIG_PATH} is not a JSON object.`);
    }
    payload = parsed as Record<string, unknown>;
  } catch (cause) {
    if (cause instanceof RuntimeConfigError) throw cause;
    throw new RuntimeConfigError(
      `Could not load ${RUNTIME_CONFIG_PATH}: ${cause instanceof Error ? cause.message : String(cause)}`,
    );
  }

  const ingestionServiceUrl = pick(payload, [
    'ingestionServiceUrl',
    'ingestionApiUrl',
    'backendUrl',
  ]);
  const enrichmentServiceUrl = pick(payload, [
    'enrichmentServiceUrl',
    'extractionServiceUrl',
    'enrichmentApiUrl',
    'extractionApiUrl',
  ]);

  const missing: string[] = [];
  if (!ingestionServiceUrl) missing.push('ingestionServiceUrl');
  if (!enrichmentServiceUrl) missing.push('enrichmentServiceUrl');
  if (missing.length > 0) {
    throw new RuntimeConfigError(
      `${RUNTIME_CONFIG_PATH} is missing: ${missing.join(', ')}.`,
    );
  }

  resolved = { ingestionServiceUrl, enrichmentServiceUrl };
  return resolved;
}

/** The loaded config. Throws if called before `loadRuntimeConfig()` has resolved. */
export function getRuntimeConfig(): RuntimeConfig {
  if (!resolved) {
    throw new RuntimeConfigError(
      'Runtime config read before it was loaded. Await loadRuntimeConfig() in main.tsx first.',
    );
  }
  return resolved;
}

/** Test seam. */
export function __setRuntimeConfigForTests(config: RuntimeConfig | null): void {
  resolved = config;
}
